// material
import React, { useEffect, useState } from 'react';
import { subDays } from 'date-fns';
import {
  Box,
  Grid,
  Container,
  Typography,
  Card,
  Stack,
  Tab,
  IconButton
} from '@mui/material';
import { TabContext, TabPanel, TabList } from '@mui/lab';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import {
  GetOrganizationDeviceStats,
  GetOrganizationTransactionStats,
  GetOrganizationMerchantStats
} from '../network/Organization';
import { getDateINYMD, getDateInddMMYYYY } from '../utils/formatTime';
import { useAuth } from '../components/customContextProviders/AuthContext';

// components
import Page from '../components/Page';
import { DateRangePickerDialog, ExportDataModal } from '../components/_dashboard/app';
import {
  SoundBoxDevicesStats,
  SoundBoxStatsItem,
  DeviceOrderdButtons
} from '../components/_dashboard/soundbox';
import SoundBoxTransactions from './SoundBoxTransactions';
import DashboardNavbar from '../layouts/dashboard/DashboardNavbar';
import DashboardSidebar from '../layouts/dashboard/DashboardSidebar';
import { SnackBarAlert } from '../components/HelperComponents';
import ContactSupportButton from '../components/ContactSupportButton';

export default function SoundBoxDashboard() {
  const [open, setOpen] = useState(false);
  const { allOrganizationList } = useAuth();
  const [transactionStatsData, setTransactionStatsData] = useState({});
  const [isDateRangeDialogOpen, setIsDateRangeDialogOpen] = useState(false);
  const [customDateSelectedString, setCustomDateSelectedString] = useState('');
  const [isExportDataModalOpen, setIsExportDataModalOpen] = useState(false);
  const [statsActive, setStatsActive] = useState('1');

  const [isSnackBarAlertOpen, setIsSnackBarAlertOpen] = useState(false);
  const [snackBarMetas, setSnackBarMetas] = useState({
    title: '',
    message: '',
    severity: 'info',
    handleClose: null
  });

  const [soundboxStatsData, setSoundboxStatsData] = useState({
    merchantStats: {},
    deviceStats: {}
  });

  useEffect(() => {
    getDashboardDeviceStats();
    getTransactionStats(getDateINYMD(new Date()), getDateINYMD(new Date()));
    getOrganizationMerchantStats();

    return () => {};
  }, []);

  const handleStatsTabChange = (_event, newValue) => {
    setTransactionStatsData({});
    if (newValue === '1') getTransactionStats(getDateINYMD(new Date()), getDateINYMD(new Date()));
    else if (newValue === '2')
      getTransactionStats(getDateINYMD(subDays(new Date(), 30)), getDateINYMD(new Date()));
    else if (newValue === '3') setIsDateRangeDialogOpen(true);

    setStatsActive(newValue);
  };

  function getDashboardDeviceStats() {
    GetOrganizationDeviceStats()
      .then((res) => {
        setSoundboxStatsData((soundboxStatsData) => ({
          ...soundboxStatsData,
          deviceStats: res?.data
        }));
      })
      .catch((_error) => {});
  }

  function getOrganizationMerchantStats() {
    GetOrganizationMerchantStats()
      .then((res) => {
        setSoundboxStatsData((soundboxStatsData) => ({
          ...soundboxStatsData,
          merchantStats: res?.data
        }));
      })
      .catch((_err) => {});
  }

  function onDateRangeSelectedAction(fromDate, todate) {
    if (fromDate === undefined || todate === undefined || fromDate === null || todate === null) {
      setSnackBarMetas((prevState) => ({
        ...prevState,
        title: "You haven't selected the Date Range.",
        severity: 'warning'
      }));
      setIsSnackBarAlertOpen(true);

      setStatsActive('1');
      getTransactionStats(getDateINYMD(new Date()), getDateINYMD(new Date()));
      return false;
    }

    setCustomDateSelectedString(
      getDateInddMMYYYY(fromDate).concat(' -to- ').concat(getDateInddMMYYYY(todate))
    );
    getTransactionStats(getDateINYMD(fromDate), getDateINYMD(todate));
  }

  function getTransactionStats(fromDate, toDate) {
    GetOrganizationTransactionStats(fromDate, toDate)
      .then((res) => {
        setTransactionStatsData(res?.data?.result?.data);
      })
      .catch((_error) => {});
  }

  return (
    <Page title="AudioCube" data-cy="soundbox-dashboard">
      <DashboardNavbar onOpenSidebar={() => setOpen(true)} />
      <DashboardSidebar isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} />

      <Container maxWidth="false">
        <Box
          data-cy="devices-controls"
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <DeviceOrderdButtons organizationData={allOrganizationList} />
          <ContactSupportButton />
        </Box>  

        <Grid
          container
          spacing={1}
          mb={1}
          columns={12}
          sx={{ display: 'flex', alignItems: 'stretch' }}
        >
          <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6 }} sx={{ display: 'flex' }}>
            <Box sx={{ width: '100%', height: '100%', display: 'flex' }} data-cy="devices-stats">
              <SoundBoxDevicesStats
                admindashboardData={soundboxStatsData?.merchantStats}
                deviceDashboardData={soundboxStatsData?.deviceStats}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6 }} sx={{ display: 'flex' }}>
            <Box
              sx={{ width: '100%', height: '100%', display: 'flex' }}
              data-cy="transaction-stats"
            >
              <Card
                sx={{
                  height: '100%',
                  width: '100%',
                  background: '#FFFFFF',
                  boxShadow: '10px 10px 19px 1px #EAEAFF',
                  borderRadius: '20px',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    typography: 'body1',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <TabContext value={statsActive}>
                    <TabList
                      sx={{
                        '& .MuiTabs-flexContainer': {
                          justifyContent: 'center'
                        },
                        px: 2,
                        pt: 1
                      }}
                      onChange={handleStatsTabChange}
                      aria-label="transaction_stats_tab"
                      data-cy="transactions-tabs"
                    >
                      <Tab label="Day" value="1" data-cy="tab-day" />
                      <Tab label="Month" value="2" data-cy="tab-month" />
                      <Tab
                        iconPosition="end"
                        label={
                          <div>
                            <Stack direction="row" spacing={1}>
                              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                                Custom Date
                              </Typography>
                              {/* <Typography
                                sx={{
                                  fontSize: 14,
                                  fontWeight: 700,
                                  backgroundColor: '#C6FFCF',
                                  borderRadius: 8,
                                  px: 1
                                }}
                                data-cy="custom-date-label"
                              >
                                {customDateSelectedString}
                              </Typography> */}
                              {customDateSelectedString !== '' ? (
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={(e) => {
                                    setCustomDateSelectedString('');
                                    handleStatsTabChange(e, '1');
                                  }}
                                  sx={{ p: 0 }}
                                  data-cy="clear-custom-date"
                                >
                                  <CloseOutlinedIcon sx={{ fontSize: 24 }} />
                                </IconButton>
                              ) : null}
                            </Stack>
                          </div>
                        }
                        value="3"
                        data-cy="tab-custom"
                      />
                    </TabList>

                    <TabPanel value="1" sx={{ flex: 1, px: 2, pb: 2 }}>
                      <SoundBoxStatsItem statsData={transactionStatsData} />
                    </TabPanel>
                    <TabPanel value="2" sx={{ flex: 1, px: 2, pb: 2 }}>
                      <SoundBoxStatsItem statsData={transactionStatsData} />
                    </TabPanel>
                    <TabPanel value="3" sx={{ flex: 1, px: 2, pb: 2 }}>
                      <SoundBoxStatsItem statsData={transactionStatsData} />
                    </TabPanel>
                  </TabContext>
                </Box>
              </Card>
            </Box>
          </Grid>
        </Grid>

        <Box>
          <Grid container spacing={3}>
            <Box sx={{ width: '100%' }} data-cy="transactions-list">
              <SoundBoxTransactions />
            </Box>
          </Grid>
        </Box>

        <ExportDataModal
          opneAssignedModal={isExportDataModalOpen}
          handleClose={() => setIsExportDataModalOpen(false)}
        />

        <SnackBarAlert
          open={isSnackBarAlertOpen}
          title={snackBarMetas?.title}
          message={snackBarMetas?.message}
          severity={snackBarMetas?.severity}
          handleClose={() => setIsSnackBarAlertOpen(false)}
        />

        <DateRangePickerDialog
          openDateRangeModal={isDateRangeDialogOpen}
          handleClose={() => setIsDateRangeDialogOpen(false)}
          onDismiss={() => {
            setStatsActive('1');
            getTransactionStats(getDateINYMD(new Date()), getDateINYMD(new Date()));
          }}
          onAction={(fromDate, toDate) => onDateRangeSelectedAction(fromDate, toDate)}
        />
      </Container>
    </Page>
  );
}
